import { useState, useEffect } from 'react';
import { Users, Briefcase, Layers } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService } from '@core/supabase/services/jobService';
import { applicationService } from '@core/supabase/services/applicationService';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { Candidate } from '@core/types';
import { Job } from '@core/types';
import { computeSkillOverlapFitment } from '@core/utils/skillFitment';

type Mode = '1:1' | '1:N' | 'N:1' | 'N:M';

export function BulkPage() {
  const { user } = useAuth();
  const [mode, setMode] = useState<Mode>('1:1');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(0);
  /** MAT-030: persist skill overlap on each new application when the job has `skills_required` */
  const [applySkillFitment, setApplySkillFitment] = useState(false);

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    Promise.all([
      candidateService.list(user.orgId),
      jobService.list(user.orgId, { status: 'open' }),
    ])
      .then(([c, j]) => {
        setCandidates(c);
        setJobs(j.filter((x) => x.client_id));
      })
      .finally(() => setLoading(false));
  }, [user?.orgId]);

  const toggleCandidate = (id: string) => {
    setSelectedCandidates((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleJob = (id: string) => {
    setSelectedJobs((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const getClientId = (job: Job) => job.client_id ?? (job as any).client?.id ?? '';

  const handleStartBatch = async () => {
    if (!user?.orgId) return;
    const cands = Array.from(selectedCandidates);
    const jbs = Array.from(selectedJobs);
    if (cands.length === 0 || jbs.length === 0) return;

    const orgId = user.orgId;

    const tryCreate = async (candidateId: string, jobId: string) => {
      const job = jobs.find((j) => j.id === jobId);
      if (!job) return false;
      const clientId = getClientId(job);
      if (!clientId) return false;
      const payload: Parameters<typeof applicationService.create>[0] = {
        org_id: orgId,
        candidate_id: candidateId,
        job_id: jobId,
        client_id: clientId,
      };
      if (applySkillFitment) {
        const candidate = candidates.find((c) => c.id === candidateId);
        if (candidate) {
          const fit = computeSkillOverlapFitment(candidate, job);
          if (fit) {
            payload.fitment_score = fit.fitment_score;
            payload.fitment_json = fit.fitment_json;
          }
        }
      }
      try {
        await applicationService.create(payload);
        return true;
      } catch {
        return false;
      }
    };

    setSubmitting(true);
    let count = 0;

    if (mode === '1:1' && cands.length === jbs.length) {
      for (let i = 0; i < cands.length; i++) {
        if (await tryCreate(cands[i], jbs[i])) count++;
      }
    } else {
      for (const cid of cands) {
        for (const jid of jbs) {
          if (await tryCreate(cid, jid)) count++;
        }
      }
    }

    setCreated(count);
    setSubmitting(false);
    setSelectedCandidates(new Set());
    setSelectedJobs(new Set());
  };

  const estimatedCount =
    mode === '1:1' ? Math.min(selectedCandidates.size, selectedJobs.size) : selectedCandidates.size * selectedJobs.size;

  return (
    <div>
      <PageHeader
        icon={Layers}
        breadcrumb={[{ label: 'Pipeline', href: '/applications' }, { label: 'Bulk process' }]}
        title="Bulk process"
        subtitle="Create multiple applications at once from selected candidates and jobs"
      />

      <div className="flex gap-2 mb-6">
        {(['1:1', '1:N', 'N:1', 'N:M'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              mode === m ? 'bg-brand-600 text-white' : 'bg-[var(--vscode-bg-tertiary)] vscode-text-secondary hover:bg-brand-500/10'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      {loading ? (
        <FullPageSpinner />
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="text-sm font-semibold vscode-text-primary mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-brand-400" /> Candidates ({selectedCandidates.size} selected)
            </h2>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {candidates.map((c) => (
                <label key={c.id} className="flex items-center gap-2 p-2 rounded hover:bg-brand-500/10 cursor-pointer">
                  <input type="checkbox" checked={selectedCandidates.has(c.id)} onChange={() => toggleCandidate(c.id)} />
                  <span className="text-sm vscode-text-primary truncate">{c.full_name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="card">
            <h2 className="text-sm font-semibold vscode-text-primary mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-brand-400" /> Jobs ({selectedJobs.size} selected)
            </h2>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {jobs.map((j) => (
                <label key={j.id} className="flex items-center gap-2 p-2 rounded hover:bg-brand-500/10 cursor-pointer">
                  <input type="checkbox" checked={selectedJobs.has(j.id)} onChange={() => toggleJob(j.id)} />
                  <span className="text-sm vscode-text-primary truncate">{j.title}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 card space-y-3">
        <label className="flex items-start gap-2 cursor-pointer text-sm vscode-text-secondary">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-[var(--vscode-border-primary)]"
            checked={applySkillFitment}
            onChange={(e) => setApplySkillFitment(e.target.checked)}
          />
          <span>
            Store skill match score on each application (candidate parsed skills vs job required skills). Jobs
            with no required skills are unchanged.
          </span>
        </label>
        <div className="flex items-center justify-between pt-1">
        <p className="text-sm vscode-text-secondary">
          {estimatedCount} application{estimatedCount !== 1 ? 's' : ''} will be created
        </p>
        <button
          className="btn-primary"
          onClick={handleStartBatch}
          disabled={submitting || selectedCandidates.size === 0 || selectedJobs.size === 0}
        >
          {submitting ? 'Creating…' : '[Process Batch]'}
        </button>
        </div>
      </div>

      {created > 0 && (
        <p className="mt-4 text-sm text-green-400">Created {created} application(s).</p>
      )}
    </div>
  );
}
